import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { AxiosError } from 'axios';

import { IconArrowLeft } from '@tabler/icons-react';
import { ButtonFieldIcon } from '@wk/components-react16';

import {
  useSdkCheckpointsCatalogQuery,
  useSiteComplianceCheckpointsQuery,
  type CheckpointSettingDto,
} from '@/domains/siteCompliance';
import { hasCheckpointVersionValue } from '@/lib/checkpointKeys';
import {
  siteComplianceApi,
  type SiteComplianceApiError,
} from '@/lib/siteComplianceApi';
import { trackSiteComplianceEvent } from '@/lib/siteComplianceTelemetry';
import {
  createSiteComplianceGetRoleProbe,
  markSiteComplianceRoleDenied,
  useInferredSiteComplianceRole,
} from '@/portals/siteCompliance/components/RoleGuard';
import { buildSiteComplianceSettingsPath } from '@/portals/siteCompliance/siteComplianceRouting';
import useToast from '@/shared/stores/toastStore';
import type { CheckpointSystemStatus } from '@/types';

import SiteCheckpointSettingsContent from './SiteCheckpointSettingsContent';
import SiteCheckpointSettingsModal from './SiteCheckpointSettingsModal';
import type {
  SiteCheckpointRow,
  SiteCheckpointSettingsState,
  SiteDateEditKind,
  SiteMaintenanceCriticality,
} from './SiteCheckpointSettingsPage.types';
import {
  buildCheckpointRows,
  getCurrentLocalDateTimeInput,
  isPastDateTimeInput,
  isTransitionAllowed,
  MAINTENANCE_CRITICALITY_OPTIONS,
  toDate,
  toLocalDateTimeInput,
} from './SiteCheckpointSettingsPage.utils';

import '@/features/settings/pages/CheckpointSettingsPage/CheckpointSettingsPage.css';

const MOBILE_MEDIA_QUERY = '(max-width: 640px)';
const CHECKPOINT_MANAGER_ROLE = 'SiteCompliance.CheckpointManager';
const checkpointManagerRoleProbe = createSiteComplianceGetRoleProbe(
  '/api/v1/system/checkpoints',
);

interface SiteCheckpointFormValues {
  comment: string;
  endDate: string;
  criticality: SiteMaintenanceCriticality;
}

interface SiteCheckpointStatusMutationVariables {
  checkpointId: string;
  checkpointVersion?: string | number;
  status: CheckpointSystemStatus;
  reason: string;
  endDate?: string;
  disabledStartDate?: string;
  disabledReason?: string;
}

const getApiErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { title?: string | null; message?: string | null; detail?: string | null }
      | string
      | undefined;

    if (typeof data === 'string') return data;
    return data?.title || data?.message || data?.detail || null;
  }

  return error instanceof Error ? error.message : null;
};

const buildStatusPatch = (data: SiteCheckpointStatusMutationVariables) => {
  const nowIso = new Date().toISOString();

  if (data.status === 'LIVE') {
    return {
      status: {
        unScored: {},
        disabled: {},
      },
    };
  }

  if (data.status === 'PREVIEW') {
    return {
      status: {
        unScored: {
          reason: data.reason,
          endDate: data.endDate,
        },
        disabled: {},
      },
    };
  }

  if (data.status === 'MAINTENANCE') {
    const maintenanceReason = /-\s*maintenance\s*$/i.test(data.reason)
      ? data.reason
      : `${data.reason} - Maintenance`;

    return {
      status: {
        unScored: {
          reason: maintenanceReason,
          endDate: data.endDate,
        },
        disabled: {
          reason:
            data.disabledReason ||
            'Disabling checkpoint since maintenance window has ended',
          startDate: data.disabledStartDate || data.endDate,
        },
      },
    };
  }

  return {
    status: {
      unScored: {},
      disabled: {
        reason: data.reason,
        startDate: nowIso,
      },
    },
  };
};

const updateSiteCheckpointStatus = async (
  data: SiteCheckpointStatusMutationVariables,
) => {
  const startedAt = Date.now();
  const response = await siteComplianceApi.put<CheckpointSettingDto>(
    `/api/v1/system/checkpoints/${encodeURIComponent(data.checkpointId)}`,
    buildStatusPatch(data),
    {
      params: {
        version: hasCheckpointVersionValue(data.checkpointVersion)
          ? data.checkpointVersion
          : undefined,
      },
    },
  );

  trackSiteComplianceEvent('SiteComplianceCheckpointUpdated', {
    checkpointId: data.checkpointId,
    durationMs: Math.round(Date.now() - startedAt),
    status: data.status,
    statusCode: response.status,
  });

  return response;
};

const SiteCheckpointSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });
  const { showToast } = useToast();

  const roleQuery = useInferredSiteComplianceRole({
    probe: checkpointManagerRoleProbe,
    role: CHECKPOINT_MANAGER_ROLE,
  });
  const isCheckpointManagerDenied = roleQuery.data === false;
  const canManageCheckpoints = !isCheckpointManagerDenied;
  const mutationTooltip = isCheckpointManagerDenied
    ? 'Requer role: SiteCompliance.CheckpointManager'
    : null;

  const {
    data: checkpointConfigsData,
    isLoading: isCheckpointConfigsLoading,
    isFetching: isCheckpointConfigsFetching,
    refetch: refetchCheckpointConfigs,
    error: checkpointConfigsError,
  } = useSiteComplianceCheckpointsQuery();
  const {
    data: checkpointCatalogData,
    isLoading: isCheckpointCatalogLoading,
    isFetching: isCheckpointCatalogFetching,
    refetch: refetchCheckpointCatalog,
    error: checkpointCatalogError,
  } = useSdkCheckpointsCatalogQuery();

  const {
    mutate: updateCheckpointStatusFn,
    isPending: isUpdatingCheckpoint,
  } = useMutation({
    mutationFn: updateSiteCheckpointStatus,
    onSuccess: () => {
      showToast('success', {
        title: 'Checkpoint status updated',
        message: 'Checkpoint status updated successfully',
      });
    },
    onError: (error: SiteComplianceApiError) => {
      if (error.response?.status === 403) {
        markSiteComplianceRoleDenied(queryClient, CHECKPOINT_MANAGER_ROLE);
      }

      showToast('error', {
        title: 'Error',
        message:
          getApiErrorMessage(error) ||
          'An error occurred while updating the checkpoint status',
      });
    },
  });

  const [state, setState] = useState<SiteCheckpointSettingsState>({
    activeCheckpoint: null,
    targetStatus: null,
    isPreviewDialogOpen: false,
    dialogMode: 'MOVE',
    dateEditKind: null,
    openedAtIso: null,
  });

  const handleStateUpdate = (newState: Partial<SiteCheckpointSettingsState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const {
    values,
    handleChange: handlePreviewChange,
    handleSubmit: handlePreviewSubmit,
    setFieldValue,
    errors,
    isValid,
  } = useFormik<SiteCheckpointFormValues>({
    initialValues: {
      comment: '',
      endDate: '',
      criticality: 'MEDIUM',
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      comment: string().test(
        'comment-required',
        'Reason is required',
        (value) => {
          if (state.dialogMode === 'MOVE') {
            return Boolean((value ?? '').toString().trim());
          }
          return true;
        },
      ),
      endDate: string()
        .test('endDate-required', 'End date is required', (value) => {
          if (state.dialogMode === 'EDIT_DATE') {
            return Boolean((value ?? '').toString().trim());
          }
          return true;
        })
        .test('endDate-valid', 'End date is invalid', (value) => {
          if (state.dialogMode !== 'EDIT_DATE') return true;
          const raw = (value ?? '').toString().trim();
          if (!raw) return true;
          return Boolean(toDate(raw));
        })
        .test('endDate-not-past', 'End date cannot be in the past', (value) => {
          if (state.dialogMode !== 'EDIT_DATE') return true;
          const raw = (value ?? '').toString().trim();
          if (!raw) return true;
          return !isPastDateTimeInput(raw);
        }),
    }),
    onSubmit: (formValues) => {
      onPreviewSubmit(formValues);
    },
  });

  const commentValue = values.comment.toString().trim();
  const endDateValue = values.endDate.toString().trim();
  const isMoveDialog = state.dialogMode === 'MOVE';
  const isEditDateDialog = state.dialogMode === 'EDIT_DATE';
  const isSaveDisabled =
    isUpdatingCheckpoint ||
    !state.activeCheckpoint?.id ||
    !state.targetStatus ||
    !isValid ||
    (isMoveDialog && commentValue.length === 0) ||
    (isEditDateDialog && endDateValue.length === 0);

  const checkpoints = useMemo(() => {
    return buildCheckpointRows(checkpointConfigsData, checkpointCatalogData);
  }, [checkpointCatalogData, checkpointConfigsData]);

  const handleBack = () => {
    navigate(buildSiteComplianceSettingsPath(location.pathname));
  };

  const openMoveModal = (
    row: SiteCheckpointRow,
    targetStatus: CheckpointSystemStatus,
  ) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const currentStatus = row.status;
    if (!isTransitionAllowed(currentStatus, targetStatus)) {
      showToast('info', {
        title: 'Invalid status transition',
        message: `Cannot move checkpoint from ${currentStatus} to ${targetStatus}.`,
      });
      return;
    }

    handleStateUpdate({
      activeCheckpoint: row,
      targetStatus,
      isPreviewDialogOpen: true,
      dialogMode: 'MOVE',
      dateEditKind: null,
      openedAtIso: new Date().toISOString(),
    });
    setFieldValue('comment', '');
    setFieldValue('endDate', '');
    setFieldValue('criticality', 'MEDIUM');
  };

  const openEditDateModal = (row: SiteCheckpointRow, kind: SiteDateEditKind) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const currentStatus = row.status ?? 'LIVE';
    const requiredStatus = kind === 'PREVIEW_END' ? 'PREVIEW' : 'MAINTENANCE';
    if (currentStatus !== requiredStatus) {
      showToast('info', {
        title: 'Invalid action',
        message: `This action is only available for checkpoints in ${requiredStatus}.`,
      });
      return;
    }

    const existingEndDateInput = toLocalDateTimeInput(row.statusTooltip?.endDate);
    const currentDateInput = getCurrentLocalDateTimeInput();

    handleStateUpdate({
      activeCheckpoint: row,
      targetStatus: requiredStatus,
      isPreviewDialogOpen: true,
      dialogMode: 'EDIT_DATE',
      dateEditKind: kind,
      openedAtIso: null,
    });
    setFieldValue('comment', '');
    setFieldValue(
      'endDate',
      existingEndDateInput && !isPastDateTimeInput(existingEndDateInput)
        ? existingEndDateInput
        : currentDateInput,
    );
    setFieldValue('criticality', 'MEDIUM');
  };

  const onPreviewModalClose = () => {
    handleStateUpdate({
      isPreviewDialogOpen: false,
      activeCheckpoint: null,
      targetStatus: null,
      dialogMode: 'MOVE',
      dateEditKind: null,
      openedAtIso: null,
    });
    setFieldValue('comment', '');
    setFieldValue('endDate', '');
    setFieldValue('criticality', 'MEDIUM');
  };

  const onPreviewSubmit = (formValues: SiteCheckpointFormValues) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const checkpointId = state.activeCheckpoint?.id || '';
    const checkpointVersion = state.activeCheckpoint?.version;
    const status = state.targetStatus;
    if (!checkpointId || !status) return;

    const addDaysIso = (baseIso: string, days: number) => {
      const base = new Date(baseIso);
      if (Number.isNaN(base.getTime())) return undefined;
      const ms = base.getTime() + days * 24 * 60 * 60 * 1000;
      return new Date(ms).toISOString();
    };

    const toIso = (input?: string) => {
      const date = toDate(input);
      if (!date) return undefined;
      return date.toISOString();
    };

    if (state.dialogMode === 'EDIT_DATE') {
      const existingReason = state.activeCheckpoint?.statusTooltip?.reason;
      if (!existingReason || !existingReason.toString().trim()) {
        showToast('error', {
          title: 'Missing reason',
          message:
            'Cannot update the date because the checkpoint has no reason set.',
        });
        return;
      }

      const endDateInput = formValues.endDate.toString().trim();
      if (isPastDateTimeInput(endDateInput)) {
        showToast('error', {
          title: 'Invalid end date',
          message: 'End date must be now or in the future.',
        });
        return;
      }

      const endDateIso = toIso(endDateInput);
      if (!endDateIso) {
        showToast('error', {
          title: 'Invalid end date',
          message: 'Please provide a valid end date.',
        });
        return;
      }

      updateCheckpointStatusFn(
        {
          checkpointId,
          checkpointVersion,
          status,
          reason: existingReason,
          endDate: endDateIso,
          disabledStartDate: status === 'MAINTENANCE' ? endDateIso : undefined,
        },
        {
          onSuccess: () => {
            onPreviewModalClose();
            refetchCheckpointConfigs();
            refetchCheckpointCatalog();
          },
          onError: () => {},
        },
      );
      return;
    }

    const openedAtIso = state.openedAtIso || new Date().toISOString();
    const criticalityConfig = MAINTENANCE_CRITICALITY_OPTIONS.find(
      (option) => option.value === formValues.criticality,
    );
    const maintenanceDays = criticalityConfig?.days ?? 7;

    const endDateIso =
      status === 'PREVIEW'
        ? addDaysIso(openedAtIso, 30)
        : status === 'MAINTENANCE'
          ? addDaysIso(openedAtIso, maintenanceDays)
          : undefined;

    updateCheckpointStatusFn(
      {
        checkpointId,
        checkpointVersion,
        status,
        reason: formValues.comment,
        endDate: endDateIso,
        disabledStartDate:
          status === 'MAINTENANCE'
            ? endDateIso
            : status === 'DISABLED'
              ? new Date().toISOString()
              : undefined,
      },
      {
        onSuccess: () => {
          onPreviewModalClose();
          refetchCheckpointConfigs();
          refetchCheckpointCatalog();
        },
        onError: () => {},
      },
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', onViewportChange);
    };
  }, []);

  useEffect(() => {
    if (checkpointConfigsError || checkpointCatalogError) {
      const configMessage =
        getApiErrorMessage(checkpointConfigsError) ||
        'Site Compliance checkpoint configuration failed to load';
      const catalogMessage =
        getApiErrorMessage(checkpointCatalogError) ||
        'Site Compliance checkpoint catalog failed to load';

      showToast('error', {
        title: 'Error',
        message: `${configMessage} - ${catalogMessage}`,
      });
    }
  }, [checkpointCatalogError, checkpointConfigsError, showToast]);

  const isLoading =
    isCheckpointConfigsLoading ||
    isCheckpointCatalogLoading ||
    isCheckpointConfigsFetching ||
    isCheckpointCatalogFetching;

  return (
    <div className="checkpoints-page flex h-full flex-col gap-4 max-sm:h-auto max-sm:gap-3 max-sm:overflow-x-hidden">
      <h1 className="flex items-center gap-2 text-2xl font-bold">
        <ButtonFieldIcon>
          <button type="button" onClick={handleBack}>
            <IconArrowLeft size={28} />
          </button>
        </ButtonFieldIcon>
        Checkpoints
      </h1>

      <div className="flex h-[calc(100vh-4.25rem)] flex-1 min-h-0 flex-col gap-4 max-sm:h-auto max-sm:flex-none">
        <div className="h-full min-h-0 w-full sm:flex-1">
          <SiteCheckpointSettingsContent
            checkpoints={checkpoints}
            isLoading={isLoading}
            isMobileViewport={isMobileViewport}
            canManageCheckpoints={canManageCheckpoints}
            mutationTooltip={mutationTooltip}
            onOpenMoveModal={openMoveModal}
            onOpenEditDateModal={openEditDateModal}
          />
        </div>
      </div>

      <SiteCheckpointSettingsModal
        isOpen={state.isPreviewDialogOpen}
        activeCheckpoint={state.activeCheckpoint}
        targetStatus={state.targetStatus}
        dialogMode={state.dialogMode}
        openedAtIso={state.openedAtIso}
        comment={values.comment}
        endDate={values.endDate}
        criticality={values.criticality}
        endDateError={state.dialogMode === 'EDIT_DATE' ? errors.endDate : undefined}
        isUpdatingCheckpoint={isUpdatingCheckpoint}
        isSaveDisabled={isSaveDisabled}
        onChange={handlePreviewChange}
        onSubmit={() => {
          handlePreviewSubmit();
        }}
        onClose={onPreviewModalClose}
      />
    </div>
  );
};

export default SiteCheckpointSettingsPage;
